<script setup>
import { reactive, ref } from "vue";

defineProps({
  disabled: { type: Boolean, default: false },
});

const STATUS_OPTIONS = ["active", "blocked", "ghosted", "deleted"];
const GENDER_OPTIONS = ["female", "male", "nonBinary"];

const mode = ref("builder"); // "builder" | "json"
const actions = reactive([]);
const actionsJson = ref("");
const message = ref("");

const safeStringify = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_) {
    return "";
  }
};

const convertToBuilder = (rawActions) => {
  const actionsArray = Array.isArray(rawActions) ? rawActions : [];
  const builderActions = [];
  for (const action of actionsArray) {
    if (!action || typeof action !== "object" || !action.type) {
      return { mode: "json", actions: [], actionsJson: safeStringify(actionsArray) };
    }
    if (action.type === "SetUserStatus") {
      builderActions.push({
        type: "SetUserStatus",
        status: action.status || "",
      });
      continue;
    }
    if (action.type === "ChangeGender") {
      builderActions.push({
        type: "ChangeGender",
        newGender: action.newGender || "",
      });
      continue;
    }
    return { mode: "json", actions: [], actionsJson: safeStringify(actionsArray) };
  }

  return {
    mode: "builder",
    actions: builderActions,
    actionsJson: safeStringify(actionsArray.length ? actionsArray : builderActions),
  };
};

const reset = (rawActions = []) => {
  const result = convertToBuilder(rawActions);
  mode.value = result.mode;
  actions.splice(0, actions.length, ...result.actions);
  actionsJson.value = result.actionsJson;
  message.value = "";
};

const addAction = () => {
  actions.push({ type: "SetUserStatus" });
};

const removeAction = (index) => {
  if (index < 0 || index >= actions.length) return;
  actions.splice(index, 1);
};

const onActionTypeChange = (action) => {
  if (!action) return;
  if (action.type === "SetUserStatus" && !action.status) {
    action.status = STATUS_OPTIONS[0];
  }
  if (action.type !== "SetUserStatus") {
    delete action.status;
  }
  if (action.type === "ChangeGender" && !action.newGender) {
    action.newGender = GENDER_OPTIONS[0];
  }
  if (action.type !== "ChangeGender") {
    delete action.newGender;
  }
};

const switchToJson = () => {
  mode.value = "json";
  actionsJson.value = safeStringify(actions);
};

const switchToBuilder = () => {
  try {
    const parsed = JSON.parse(actionsJson.value || "[]");
    const result = convertToBuilder(parsed);
    if (result.mode === "json") {
      throw new Error(
        "Only SetUserStatus and ChangeGender actions are supported in builder mode."
      );
    }
    mode.value = "builder";
    actions.splice(0, actions.length, ...result.actions);
    message.value = "";
  } catch (error) {
    message.value = error.message || "Could not switch to builder mode.";
  }
};

const prettifyJson = () => {
  try {
    const parsed = JSON.parse(actionsJson.value || "[]");
    actionsJson.value = safeStringify(parsed);
    message.value = "";
  } catch (error) {
    message.value = "Actions JSON is invalid.";
  }
};

const getPayload = () => {
  if (mode.value === "builder") {
    return actions
      .map((action) => {
        if (!action?.type) return null;
        if (action.type === "SetUserStatus") {
          const status = (action.status || "").trim();
          if (!status) {
            throw new Error("Status is required for SetUserStatus.");
          }
          return { type: "SetUserStatus", status };
        }
        if (action.type === "ChangeGender") {
          const newGender = (action.newGender || "").trim();
          if (!newGender) {
            throw new Error("Gender is required for ChangeGender.");
          }
          return { type: "ChangeGender", newGender };
        }
        return null;
      })
      .filter(Boolean);
  }

  try {
    const parsed = JSON.parse(actionsJson.value || "[]");
    if (!Array.isArray(parsed)) {
      throw new Error("Actions JSON must be an array.");
    }
    parsed.forEach((action, index) => {
      if (!action || typeof action !== "object" || !action.type) {
        throw new Error(`Action #${index + 1} is missing a type.`);
      }
    });
    return parsed;
  } catch (error) {
    throw new Error(error.message || "Invalid actions JSON.");
  }
};

defineExpose({ reset, getPayload });
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
      <label class="form-label text-sm fw-semibold mb-0">Actions</label>
      <div class="d-flex gap-2 flex-wrap">
        <button
          v-if="mode === 'builder'"
          class="btn btn-sm btn-outline-secondary mb-0"
          type="button"
          :disabled="disabled"
          @click="switchToJson"
        >
          Edit JSON
        </button>
        <button
          v-else
          class="btn btn-sm btn-outline-secondary mb-0"
          type="button"
          :disabled="disabled"
          @click="switchToBuilder"
        >
          Use builder
        </button>
        <button
          v-if="mode === 'builder'"
          class="btn btn-sm btn-outline-success mb-0"
          type="button"
          :disabled="disabled"
          @click="addAction"
        >
          Add action
        </button>
        <button
          v-else
          class="btn btn-sm btn-outline-secondary mb-0"
          type="button"
          :disabled="disabled"
          @click="prettifyJson"
        >
          Prettify JSON
        </button>
      </div>
    </div>

    <div v-if="message" class="alert alert-danger py-2 px-3">
      <span class="text-sm">{{ message }}</span>
    </div>

    <div v-if="mode === 'builder'">
      <div v-if="!actions.length" class="text-xs text-secondary mb-2">
        No actions. Rule will match and log only.
      </div>
      <div
        v-for="(action, index) in actions"
        :key="index"
        class="border rounded p-3 mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select
            class="form-select form-select-sm w-auto"
            v-model="action.type"
            :disabled="disabled"
            @change="onActionTypeChange(action)"
          >
            <option value="SetUserStatus">Set user status</option>
            <option value="ChangeGender">Change gender</option>
          </select>
          <button
            class="btn btn-sm btn-outline-danger mb-0"
            type="button"
            :disabled="disabled"
            @click="removeAction(index)"
          >
            Remove
          </button>
        </div>
        <div v-if="action.type === 'SetUserStatus'" class="mt-2">
          <label class="form-label text-xs">Status</label>
          <select
            class="form-select form-select-sm w-auto"
            v-model="action.status"
            :disabled="disabled"
          >
            <option value="" disabled>Select status</option>
            <option v-for="status in STATUS_OPTIONS" :key="status" :value="status">
              {{ status }}
            </option>
          </select>
        </div>
        <div v-else-if="action.type === 'ChangeGender'" class="mt-2">
          <label class="form-label text-xs">Gender</label>
          <select
            class="form-select form-select-sm w-auto"
            v-model="action.newGender"
            :disabled="disabled"
          >
            <option value="" disabled>Select gender</option>
            <option v-for="gender in GENDER_OPTIONS" :key="gender" :value="gender">
              {{ gender }}
            </option>
          </select>
        </div>
        <div v-else class="text-xs text-secondary mt-2">
          No additional fields.
        </div>
      </div>
    </div>
    <div v-else>
      <textarea
        v-model="actionsJson"
        class="form-control font-monospace"
        rows="6"
        :disabled="disabled"
        placeholder='[{"type":"SetUserStatus","status":"blocked"}]'
      ></textarea>
      <p class="text-xs text-secondary mt-1">
        Provide an array of action objects. Unknown action types can be sent directly with
        JSON mode.
      </p>
    </div>
  </div>
</template>
